import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const navItems = [
  { label: "Accueil", path: "/" },
  { label: "À propos", path: "/a-propos" },
  {
    label: "Propriétaires",
    children: [
      { label: "Service de conciergerie", path: "/conciergerie" },
      { label: "Service de sous-location", path: "/sous-location-conciergerie" },
      { label: "Nos tarifs", path: "/tarifs" },
      { label: "Demander un devis", path: "/devis" },
    ],
  },
  {
    label: "Voyageurs",
    children: [
      { label: "Nos locations", path: "/nos-locations" },
      { label: "Demander une réservation", path: "/reservation" },
    ],
  },
  {
    label: "Les villes",
    children: [
      { label: "Conciergerie à Aigues-Mortes", path: "/conciergerie-aigues-mortes" },
      { label: "Conciergerie à La Grande-Motte", path: "/conciergerie-la-grande-motte" },
      { label: "Conciergerie à Carnon", path: "/conciergerie-carnon" },
      { label: "Conciergerie au Grau-du-Roi", path: "/conciergerie-grau-du-roi" },
    ],
  },
  { label: "Contact", path: "/contact" },
  { label: "Actualités", path: "/nos-actualites" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
          : "bg-white"
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20 sm:h-[88px]">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-2 shrink-0">
            <img
              src="/images/logo.png"
              alt="Golden Sea Conciergerie"
              className="h-16 sm:h-[76px] w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item, i) =>
              item.children ? (
                <div
                  key={i}
                  className="relative group"
                  onMouseEnter={() => setOpenDropdown(i)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    data-testid={`navbar-${item.label.toLowerCase().replace(/\s/g, '-')}-dropdown`}
                    className="flex items-center gap-1 px-3 py-2 text-[13px] font-medium text-[#111] hover:text-[#d4af37] transition-colors duration-200"
                  >
                    {item.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openDropdown === i ? 'rotate-180' : ''}`} />
                  </button>
                  {/* Invisible bridge to prevent gap */}
                  <div className="absolute top-full left-0 h-3 w-full" />
                  <div
                    className={`absolute top-full left-0 pt-2 min-w-[240px] transition-all duration-200 ${
                      openDropdown === i
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-2 pointer-events-none"
                    }`}
                  >
                    <div className="bg-white rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-[#f1eee6] overflow-hidden">
                    {item.children.map((child, j) => (
                      <Link
                        key={j}
                        to={child.path}
                        className="block px-4 py-3 text-sm text-[#111] hover:bg-[#fbfaf7] hover:text-[#d4af37] transition-colors duration-200"
                      >
                        {child.label}
                      </Link>
                    ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={i}
                  to={item.path}
                  className={`px-3 py-2 text-[13px] font-medium transition-colors duration-200 ${
                    location.pathname === item.path
                      ? "text-[#d4af37]"
                      : "text-[#111] hover:text-[#d4af37]"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          {/* Devis CTA - Desktop */}
          <div className="hidden lg:flex items-center ml-2">
            <Link
              to="/devis"
              className="navbar-devis-shimmer relative inline-flex items-center gap-2 text-[13px] px-5 py-2.5 bg-[#1a1a1a] text-white font-bold rounded-lg hover:bg-[#111] transition-all duration-300 overflow-hidden"
              data-testid="navbar-devis-cta"
            >
              <span className="relative z-10">Devis gratuit</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-open-button"
            className="lg:hidden p-2 text-[#111]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-white border-t border-[#f1eee6] ${
          mobileOpen ? "max-h-[80vh] overflow-y-auto" : "max-h-0"
        }`}
      >
        <div className="section-container py-4 space-y-1">
          {navItems.map((item, i) =>
            item.children ? (
              <div key={i}>
                <button
                  onClick={() => setOpenDropdown(openDropdown === i ? null : i)}
                  className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-[#111]"
                >
                  {item.label}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === i ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-200 ${openDropdown === i ? 'max-h-40' : 'max-h-0'}`}>
                  {item.children.map((child, j) => (
                    <Link
                      key={j}
                      to={child.path}
                      className="block pl-8 pr-3 py-2.5 text-sm text-[#555] hover:text-[#d4af37]"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={i}
                to={item.path}
                className={`block px-3 py-3 text-sm font-medium ${
                  location.pathname === item.path ? "text-[#d4af37]" : "text-[#111]"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
          {/* Mobile Devis CTA */}
          <Link to="/devis" className="block mx-3 mt-3 bg-[#1a1a1a] text-white font-bold rounded-lg text-center text-sm py-3 hover:bg-[#111] transition-all duration-300">
            Demander un devis gratuit
          </Link>
        </div>
      </div>
    </nav>
  );
}
